import { usePostHog } from 'posthog-js/react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { NotFoundScreen } from '@/screens/404Screen'
import { BillingScreen } from '@/screens/BillingScreen/BillingScreen'
import { ChangePasswordScreen } from '@/screens/ChangePasswordScreen'
import { ContentModuleFormScreen } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { ContentModuleScreen } from '@/screens/ContentModuleScreen/ContentModuleScreen'
import { ContentModulesScreen } from '@/screens/ContentModulesScreen'
import { ForgotPasswordScreen } from '@/screens/ForgotPasswordScreen'
import { HelpScreen } from '@/screens/HelpScreen'
import { LoginScreen } from '@/screens/LoginScreen'
import { MetricsScreen } from '@/screens/MetricsScreen'
import { OnboardingScreen } from '@/screens/OnboardingScreen'
import { PeopleScreen } from '@/screens/PeopleScreen'
import { ResetPasswordScreen } from '@/screens/ResetPasswordScreen'
import { SettingsScreen } from '@/screens/SetupScreen/SettingsScreen'
import { SidekicksScreen } from '@/screens/SidekicksScreen'
import { SponsorSelectionScreen } from '@/screens/SponsorSelectionScreen'
import type { StringWithAutocomplete } from '@/types/helpers'
import type { RouteDef, RouteRedirectResponse, RouteRenderConditionResponse } from '@/utils/navigation'
import { getRoutePath } from '@/utils/navigation'

export const routes = {
  home: {
    path: '/',
    renderConditionFn: homeRedirect,
    routes: {
      onboarding: { path: '/onboarding', component: OnboardingScreen, renderConditionFn: onboardingRenderCondition },
      auth: {
        path: '/auth',
        renderConditionFn: authRenderCondition,
        redirectFn: authRedirect,
        routes: {
          login: { path: '/login', component: LoginScreen },
          sponsorSelection: {
            path: '/select-sponsor',
            component: SponsorSelectionScreen,
            renderConditionFn: sponsorSelectionRenderCondition,
          },
          changePassword: { path: '/change-password', component: ChangePasswordScreen },
          forgotPassword: { path: '/forgot-password', component: ForgotPasswordScreen },
          resetPassword: { path: '/reset-password', component: ResetPasswordScreen },
          signup: { path: '/signup' },
        },
      },
      dashboard: {
        path: '/dashboard',
        renderConditionFn: dashboardRenderCondition,
        redirectFn: disabledPagesRedirect,
        routes: {
          gettingStarted: { path: '/getting-started', redirectFn: disabledPagesRedirect },
          sidekicks: {
            path: '/expert-sidekicks',
            component: SidekicksScreen,
            renderConditionFn: sponsorRenderCondition,
            routes: {
              sidekicksForm: { path: '/:id', component: NotFoundScreen },
            },
          },
          metrics: { path: '/metrics', component: MetricsScreen },
          contentModules: {
            path: '/modules',
            component: ContentModulesScreen,
            routes: {
              contentModuleFormEdit: { path: '/:id/edit', component: ContentModuleFormScreen },
              contentModuleFormNew: { path: '/new', component: ContentModuleFormScreen },
              contentModule: { path: '/:id', component: ContentModuleScreen },
            },
          },
          help: { path: '/help', component: HelpScreen },
          billing: { path: '/billing', component: BillingScreen, renderConditionFn: sponsorRenderCondition },

          people: {
            path: '/people',
            component: PeopleScreen,
            renderConditionFn: sponsorRenderCondition,
          },
          settings: {
            path: '/settings',
            component: SettingsScreen,
            renderConditionFn: sponsorRenderCondition,
          },
        },
      },
    },
  },
} as const satisfies RouteDef['routes']

export type RouteParams = {
  home: undefined

  onboarding: undefined

  auth: undefined
  login: undefined
  sponsorSelection: undefined
  signup: undefined
  changePassword: undefined
  forgotPassword: undefined
  resetPassword: undefined

  dashboard: undefined
  gettingStarted: undefined
  sidekicks: undefined
  sidekicksForm: { id: StringWithAutocomplete<'new'> }
  contentModules: undefined
  contentModule: { id: string }
  contentModuleFormNew: undefined
  contentModuleFormEdit: { id: string }
  metrics: undefined
  help: undefined
  setup: undefined
  billing: undefined
  people: undefined
  settings: undefined
}

function logUser(_page: string) {
  const posthog = usePostHog()
  const { authenticationStore } = useGlobalState()
  const { user } = authenticationStore
  if (user) {
    posthog?.identify(user?.userId, {
      isSponsor: user?.isSponsor,
      isSponsorAdmin: user?.isSponsorAdmin,
      sponsorId: user?.sponsorId,
      sponsorName: user?.sponsorName,
    })
  }
}

function homeRedirect(): RouteRedirectResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated, ready } = authenticationStore

  if (isAuthenticated) {
    // Wait for authentication store to be fully ready (including setup status check)
    if (!ready) {
      return { redirectTo: getRoutePath('auth') } // Stay on current page while loading
    }

    // Users in setup or not both go to dashboard - setup checklist will show automatically
    return { redirectTo: getRoutePath('dashboard') }
  } else {
    return { redirectTo: getRoutePath('auth') }
  }
}

function authRedirect(): RouteRedirectResponse {
  const { authenticationStore } = useGlobalState()
  const { ready, isAuthenticated, isInSetup, pendingSponsorSelection } = authenticationStore

  // Wait for authentication store to be fully ready
  if (!ready) {
    return { redirectTo: getRoutePath('login') } // Stay on login while loading
  }

  // If not authenticated, stay on login
  if (!isAuthenticated) {
    return { redirectTo: getRoutePath('login') }
  }

  // User is authenticated - check where they should go
  if (pendingSponsorSelection) {
    return { redirectTo: getRoutePath('sponsorSelection') }
  }

  if (isInSetup) {
    return { redirectTo: getRoutePath('contentModules') }
  }
  return { redirectTo: getRoutePath('dashboard') }
}

function sponsorSelectionRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated, ready, isInSetup, pendingSponsorSelection } = authenticationStore

  console.log('sponsorSelectionRenderCondition:', {
    pendingSponsorSelection,
    isAuthenticated,
    ready,
  })

  // Only render this page if sponsor selection is actually pending
  if (pendingSponsorSelection) {
    return true // Render the sponsor selection screen
  }

  // If no sponsor selection pending, redirect based on auth state
  if (isAuthenticated && ready) {
    if (isInSetup) {
      return { redirectTo: getRoutePath('contentModules') }
    }
    return { redirectTo: getRoutePath('dashboard') }
  }

  // Not authenticated, go to login
  return { redirectTo: getRoutePath('login') }
}

function authRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated, ready, isInSetup, pendingSponsorSelection, isSponsorAdmin, setupProgress } =
    authenticationStore
  logUser('authRenderCondition')

  console.log('authRenderCondition:', {
    isAuthenticated,
    ready,
    isInSetup,
    pendingSponsorSelection,
  })

  // Wait for authentication store to be fully ready before checking any state
  if (!ready) {
    return true // Stay on current page while loading
  }

  // Not authenticated - allow access to login/auth pages
  if (!isAuthenticated) {
    return true
  }

  // User is authenticated - check where they should go
  if (pendingSponsorSelection) {
    console.log('Redirecting to sponsor selection screen')
    return { redirectTo: getRoutePath('sponsorSelection') }
  }

  if (isInSetup) {
    // If sponsor admin hasn't completed the first setup step (defineTopics), redirect to settings
    if (isSponsorAdmin && !setupProgress.defineTopics) {
      return { redirectTo: getRoutePath('settings') }
    }

    // Otherwise, go to content modules (normal setup flow)
    return { redirectTo: getRoutePath('contentModules') }
  }

  return { redirectTo: getRoutePath('dashboard') }
}

function dashboardRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated } = authenticationStore
  // logUser('dashboardRenderCondition')

  return isAuthenticated || { redirectTo: getRoutePath('auth') }
}

function disabledPagesRedirect(): RouteRedirectResponse {
  return { redirectTo: getRoutePath('contentModules') }
}

function sponsorRenderCondition(): RouteRenderConditionResponse {
  const { authenticationStore } = useGlobalState()
  const { isAuthenticated, isSponsorAdmin } = authenticationStore
  // logUser('sponsorRenderCondition')

  return !isAuthenticated ? { redirectTo: getRoutePath('auth') } : isAuthenticated && isSponsorAdmin
}

function onboardingRenderCondition(): RouteRenderConditionResponse {
  // Allow access to onboarding for all users (authenticated or not)
  return true
}
